import React, {useState, useCallback} from 'react';
import styles from './StepScoring.module.scss';
import {cn} from '../../../../../../../utils';
import apiClientConfig from "../../../../../../../configs/app";
import {useWizardContext} from '../../../context';
import {WizardStep} from '../../../types';
import {StepStatus} from '../../../../../../../api-client';
import {Button, Image} from '../../../../../../../components';
import {createBorrowingOnboardingManager} from '../../../../../../../di';
import {useErrorCallback} from '../../../../../../../hooks';
import clockIcon from 'assets/images/svg/clock.svg';
import stepApprovedIcon from 'assets/images/svg/step-approved.svg';

const StepScoring = () => {
  const {backendCurrentStep, onboardingStepStatus, setActiveStep, refreshOnboardingState} = useWizardContext();
  const handleError = useErrorCallback();
  const [notifyLoading, setNotifyLoading] = useState(false);

  const isScoringRejected =
    backendCurrentStep === WizardStep.SCORING &&
    onboardingStepStatus === StepStatus.REJECTED;

  const isScoringWaitingApproval =
    backendCurrentStep === WizardStep.SCORING &&
    onboardingStepStatus === StepStatus.WAITING_APPROVAL;

  const isScoringPassed =
    backendCurrentStep !== null && backendCurrentStep > WizardStep.SCORING;

  const isOnFirstOrSecondStep =
    backendCurrentStep !== null && backendCurrentStep <= WizardStep.VERIFICATION;

  const handleNotifyManager = useCallback(async () => {
    setNotifyLoading(true);
    try {
      const manager = await createBorrowingOnboardingManager();
      await manager.requestApproval();
      await refreshOnboardingState({silent: true, keepCurrentStep: true});
    } catch (err) {
      await handleError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setNotifyLoading(false);
    }
  }, [refreshOnboardingState, handleError]);

  if (isScoringPassed) {
    return (
      <div className={styles.stepScoring}>
        <div className={styles.scoringApprovedCard}>
          <Image src={stepApprovedIcon} className={styles.scoringApprovedIcon} alt="" aria-hidden />
          <div className={styles.scoringApprovedContext}>
            <h2 className={styles.scoringApprovedTitle}>Скоринг пройден</h2>
            <p className={styles.scoringApprovedText}>
              Документы приняты и одобрены. Вы можете перейти к следующему шагу.
            </p>
          </div>
          <Button
            type="button"
            className={styles.scoringApprovedButton}
            onClick={() => setActiveStep(WizardStep.DOCUMENTS)}
          >
            Перейти к следующему шагу
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepScoring}>
      {isScoringWaitingApproval && (
        <div className={styles.waitingApprovalCard}>
          <h2 className={styles.waitingApprovalTitle}>Документы на рассмотрении</h2>
          <div className={styles.waitingApprovalRow}>
            <Image src={clockIcon} className={styles.waitingApprovalIcon} alt="" aria-hidden />
            <p className={styles.waitingApprovalText}>
              Обычно рассмотрение занимает 1-2 рабочих дня
            </p>
          </div>
        </div>
      )}
      {isScoringRejected && (
        <div className={styles.scoringRejectedCard}>
          <h2 className={styles.scoringRejectedTitle}>Вы не прошли скоринг</h2>
          <p className={styles.scoringRejectedText}>
            К сожалению, предоставленных документов не хватило для прохождения скоринга.
          </p>
          <p className={styles.scoringRejectedText}>
            Предоставьте недостающие документы на почту{' '}
            <a href={`mailto:${apiClientConfig.email}`} className={styles.scoringRejectedLink}>
              {apiClientConfig.email}
            </a>
            {' '}для завершения скоринга.
          </p>
        </div>
      )}
      <p className={styles.introText}>
        Для прохождения этого этапа Вам необходимо направить документы на электронную почту платформы{' '}
        <a href={`mailto:${apiClientConfig.email}`} className={styles.scoringRejectedLink}>
          {apiClientConfig.email}
        </a>
        .
      </p>
      <p className={styles.introText}>
        Список документов, необходимых для проведения скоринга на Платформе:
      </p>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Информация о компании-заемщике:
        </h3>
        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>
            Бухгалтерская (финансовая) отчётность
          </div>
          <ul className={styles.infoList}>
            <li>
              бухгалтерский баланс и отчёт о финансовых результатах за последний завершённый финансовый год
            </li>
            <li>
              бухгалтерский баланс и отчёт о финансовых результатах на последнюю отчётную квартальную дату.
            </li>
          </ul>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>
            Карточки счета 51 (с разбивкой по субсчетам и указанием ИНН контрагентов)
          </div>
          <ul className={styles.infoList}>
            <li>за текущий календарный год;</li>
            <li>за 2 завершённых предшествующих года</li>
          </ul>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>
            Расшифровка оборотно-сальдовой ведомости по счёту 62 в разбивке за последние 12 месяцев
          </div>
          <ul className={styles.infoList}>
            <li>по субсчетам</li>
            <li>по каждому контрагенту (с указанием ИНН контрагентов)</li>
          </ul>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>
            Расшифровка оборотно-сальдовой ведомости по счёту 66,67 в разбивке за последние 12 месяцев
          </div>
          <ul className={styles.infoList}>
            <li>по субсчетам</li>
            <li>кредиторам (с указанием ИНН)</li>
          </ul>
        </div>
      </div>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Информация об обеспечении:
        </h3>
        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>
            Договор с Дебитором, на основании которого возникла дебиторская 
            задолженность, включая:
          </div>
          <ul className={styles.infoList}>
            <li>все приложения и спецификации;</li>
            <li>дополнительные соглашения;</li>
            <li>заявки, заказы и иные документы, являющиеся неотъемлемой частью договора.</li>
          </ul>
        </div>
        <div className={styles.infoCard}>
          <div className={styles.infoCardTitle}>
            Первичные учетные документы, подтверждающие факт исполнения 
            обязательств Заёмщиком перед Дебитором и возникновение задолженности, 
            в том числе:
          </div>
          <ul className={styles.infoList}>
            <li>акты выполненных работ;</li>
            <li>универсальные передаточные документы (УПД);</li>
            <li>товарные накладные;</li>
            <li>
              иные первичные документы, оформленные в соответствии с требованиями законодательства 
              Российской Федерации.
            </li>
          </ul>
        </div>
        <div className={styles.infoCard}>
          <div className={cn(styles.infoCardTitle, styles.infoCardTitleOnly)}>
            Акт сверки взаиморасчётов с Дебитором
          </div>
        </div>
      </div>
      <div className={styles.additionalSection}>
        <h3 className={styles.sectionTitle}>
          Дополнительно:
        </h3>

        <div className={styles.additionalCard}>
          <p className={styles.additionalCardText}>
            Согласие на получение кредитного отчета из 
            бюро кредитных историй (БКИ)
          </p>
        </div>

        {!isScoringWaitingApproval && (
          <div className={styles.notifyBanner}>
            <p className={styles.notifyBannerText}>
              После отправки документов для ускорения процесса рассмотрения Вы можете отправить уведомление об отправке документов менеджеру платформы
            </p>
            <Button
              type="button"
              className={styles.notifyBannerButton}
              disabled={notifyLoading || isOnFirstOrSecondStep}
              onClick={handleNotifyManager}
            >
              {notifyLoading ? 'Отправка…' : 'Уведомить менеджера'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepScoring;
